import React from 'react';
import { useNavigate } from 'react-router-dom';
import LandingNav from '../../../components/LandingNav';
import LandingFooter from '../../../components/LandingFooter';
import { BLOG_POSTS } from '../../../config/blog-posts';
import '../../../styles/blog.css';

const creatorLinks = [
  {
    id: 'creator-1',
    label: '@bartoli',
    url: 'https://www.tiktok.com/@bartoli',
    stats: ['1.9M followers'],
  },
  {
    id: 'creator-2',
    label: '@d0ughjoe',
    url: 'https://www.tiktok.com/@d0ughjoe',
    stats: ['3.3M followers'],
  },
];

export default function PapaJohnsBlogPage() {
  const navigate = useNavigate();
  const post = BLOG_POSTS.find((p) => p.slug === 'papajohns');

  if (!post) {
    return (
      <div className="landing-page">
        <LandingNav />
        <main className="blog-container">
          <div className="blog-header">
            <h1 className="blog-title">Blog post not found</h1>
            <p className="blog-subtitle">This post isn't available yet.</p>
            <div className="blog-header-actions">
              <button className="blog-btn secondary" onClick={() => navigate('/blog')}>
                Back to Blog
              </button>
              <button className="blog-btn primary" onClick={() => navigate('/contact')}>
                Contact
              </button>
            </div>
          </div>
        </main>
        <LandingFooter />
      </div>
    );
  }

  return (
    <div className="landing-page">
      <LandingNav />
      <main className="blog-container">
        <header className="blog-post-hero">
          <button className="blog-backlink" onClick={() => navigate('/blog')}>
            ← Back to Blog
          </button>

          <div className="blog-post-meta">
            {post.logoSrc && (
              <div className="blog-post-logo">
                <img src={post.logoSrc} alt={post.logoAlt || post.brand || 'Brand'} />
              </div>
            )}
            <div>
              <div className="blog-post-kicker">
                {post.brand ? `${post.brand} case study` : 'Blog'} {post.dateLabel ? `• ${post.dateLabel}` : ''}
              </div>
              <h1 className="blog-post-title">{post.title}</h1>
              <p className="blog-post-excerpt">{post.excerpt}</p>
            </div>
          </div>
        </header>

        <article className="blog-post-body">
          <section className="blog-post-section blog-toc">
            <div className="blog-toc-title">Table of Contents</div>
            <ol>
              <li>
                <a href="#thesis">The Thesis: Employees as Distributed Media Channels</a>
              </li>
              <li>
                <a href="#volume-deficit">The Volume Deficit: Why Brands Need to Leverage the Frontline</a>
              </li>
              <li>
                <a href="#cost-arbitrage">The Cost Arbitrage: Media Value vs. Paid Spend</a>
              </li>
              <li>
                <a href="#why-it-works">Why It Works: Trust, Network Effects, and Competitor Defense</a>
              </li>
              <li>
                <a href="#operationalizing">Operationalizing the Engine: Why You Need an EGC Operating System</a>
              </li>
              <li>
                <a href="#takeaways">Strategic Takeaways for Brand Leaders and Franchisees</a>
              </li>
              <li>
                <a href="#compounding">The Compounding Advantage</a>
              </li>
              <li>
                <a href="#content-under-analysis">Creators and Content Under Analysis</a>
              </li>
            </ol>
          </section>

          <section className="blog-post-section blog-callout">
            <p style={{ marginTop: 0 }}>
              <strong>Premise:</strong> In a saturated market where Papa John's competes for dominance across more than 3,000 locations in the US, the battle for the next slice is no longer won through sheer footprint alone. In an industry defined by thin margins and aggressive customer acquisition costs, the most successful brands are those that convert every internal operational touchpoint into a market-facing opportunity. For Papa John's, the strategic deployment of employee-generated content (EGC) has shifted from a social media byproduct to a core competitive differentiator that separates the brand from its legacy rivals.
            </p>
            <p>
              In the modern attention economy, the most valuable media real estate is not a 30-second spot during the Super Bowl; it is the six inches of glass in a consumer's hand. For years, CMOs have chased authenticity by hiring influencers to mimic the feel of organic content. But while brands were busy buying influence, Papa John's was busy scaling it from within.
            </p>
            <p style={{ marginBottom: 0 }}>
              Through the rise of creators like <a href="https://www.tiktok.com/@bartoli" target="_blank" rel="noreferrer">@bartoli</a> (1.9M followers) and <a href="https://www.tiktok.com/@d0ughjoe" target="_blank" rel="noreferrer">@d0ughjoe</a> (3.3M followers), Papa John's has unlocked a non-linear competitive advantage. In 2025 alone, these two individuals generated over 350 million organic views for the brand. This was not the result of a high-gloss agency campaign. It was the result of a shift in strategic mindset. Employees are no longer just operators; they are distributed media channels.
            </p>
          </section>

          <section className="blog-post-section" id="thesis">
            <h2>1. The Thesis: Employees as Distributed Media Channels</h2>
            <p>
              The shift from 2024 to 2026 has been clear: Trust is local, not corporate. Employees are no longer just operators executing a manual; they are distributed media channels. When an employee like Bartoli or Joe posts, they are not marketing for Papa John's. They are documenting their expertise. This shift moves a brand from Interruptive Media (ads) to Embedded Media (culture).
            </p>
            <p>
              By systemizing employee storytelling, Papa John's has achieved a level of Brand Fusion where the employee identity and the brand relevance become indistinguishable.
            </p>
          </section>

          <section className="blog-post-section" id="volume-deficit">
            <h2>2. The Volume Deficit: Why Brands Need to Leverage the Frontline</h2>
            <p>
              The 2026 social algorithm is a hungry machine. For a brand to remain relevant on the For You Page (FYP), the baseline requirement has shifted from quality over quantity to <strong>quantity over quality</strong>. Most corporate marketing departments are structurally incapable of meeting this demand. A traditional team might manage 2 to 4 posts per week due to approval loops and production timelines.
            </p>
            <p>
              Contrast that with Dough Joe and Bartoli, who posted almost every single day in 2025, sometimes hitting the feed multiple times in 24 hours.
            </p>
            <ul>
              <li>
                <strong>The Velocity Gap:</strong> While a brand is waiting for a creative director to approve a script, Joe has already filmed, edited, and published three videos using trending sounds.
              </li>
              <li>
                <strong>The Cost of Frequency:</strong> For a brand to produce 400+ high-engagement videos a year via an agency, the budget would be astronomical. By leveraging EGC, Papa John's achieves a posting frequency that is effectively free while being 10x more effective than a sponsored ad.
              </li>
              <li>
                <strong>The Algorithmic Reward:</strong> Platforms reward accounts that provide a consistent stream of data. By posting daily, employees keep the brand locked into the algorithm, ensuring they are constantly top of mind for consumers.
              </li>
            </ul>
          </section>

          <section className="blog-post-section" id="cost-arbitrage">
            <h2>3. The Cost Arbitrage: Media Value vs. Paid Spend</h2>
            <p>
              To achieve 350M views via TikTok Paid Ads (at a conservative $5 CPM), a brand would spend <strong>$1.75 million in distribution alone</strong>, excluding the hundreds of thousands in production costs. Papa John's achieved this with zero incremental media spend and zero production budget.
            </p>
            <p>This is the ultimate earned media flywheel.</p>
            <table className="blog-table">
              <thead>
                <tr>
                  <th>Metric</th>
                  <th>EGC Efficiency (2025)</th>
                  <th>Traditional Paid Equivalent</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Total Organic Views</td>
                  <td>350M+</td>
                  <td>~$1,750,000 (at $5 CPM)</td>
                </tr>
                <tr>
                  <td>Production Cost</td>
                  <td>$0 (Mobile-first)</td>
                  <td>~$250,000+ (Agency fees)</td>
                </tr>
                <tr>
                  <td>Engagement Rate</td>
                  <td>8–10x higher than corporate</td>
                  <td>Benchmarked at 1–2%</td>
                </tr>
                <tr>
                  <td>Viral Outliers</td>
                  <td><a href="#content-under-analysis">28.5M Views (Single post)</a></td>
                  <td>High-stakes media buy required</td>
                </tr>
              </tbody>
            </table>
          </section>

          <section className="blog-post-section" id="why-it-works">
            <h2>4. Why It Works: Trust, Network Effects, and Competitor Defense</h2>
            <p>The success of EGC is about the psychology of the feed:</p>
            <ul>
              <li>
                <strong>Radical Transparency:</strong> <a href="#content-under-analysis">Bartoli's videos</a>, like the one explaining why he remade a pizza despite an outrageous customer request, act as a 60-second masterclass in brand values. It proves the Customer First mantra more effectively than any corporate mission statement.
              </li>
              <li>
                <strong>The Un-Ad Factor:</strong> When <a href="#content-under-analysis">Dough Joe responds to a comment</a> about his minimum wage job by showcasing his 6-year journey, his college support, and his first car, he is not just selling pizza. He is selling the brand soul. This is employer branding that converts into consumer trust.
              </li>
              <li>
                <strong>Defensive Moat:</strong> When a competitor like Domino's engages (e.g., commenting "Joe better watch his back"), and <a href="#content-under-analysis">Joe responds with a high-skill video garnering 13.6M views</a>, the brand has won a war it did not even have to show up for.
              </li>
              <li>
                <strong>Algorithm-First Product R&D:</strong> <a href="#content-under-analysis">Bartoli's "boneless pizza" video (2.7M views)</a> functioned as a low-cost focus group. By asking "Should we bring this back?", the brand gauges demand in real-time before spending a single dollar on supply chain or R&D.
              </li>
            </ul>
          </section>

          <section className="blog-post-section" id="operationalizing">
            <h2>5. Operationalizing the Engine: Why You Need an EGC Operating System</h2>
            <p>
              The primary barrier for most brands is not a lack of talented employees. It is a lack of infrastructure. Papa John's success proves that EGC requires a shift from <strong>control to governance</strong>.
            </p>
            <p>
              This is where <strong>CrewCast</strong> becomes the essential operating system for the modern brand leader. To scale a program like this across thousands of locations, a brand needs a centralized way to manage decentralized creators:
            </p>
            <ul>
              <li>
                <strong>Discovery and Identification:</strong> Finding the Joes and Bartolis in your organization before they are discovered by competitors.
              </li>
              <li>
                <strong>Content Guardrails:</strong> Providing the Vibe Guidelines that allow for authenticity while protecting brand safety and food safety standards.
              </li>
              <li>
                <strong>Direct Incentivization:</strong> Rewarding the frontline for their media value, turning them from operators into stakeholders in the brand digital growth.
              </li>
              <li>
                <strong>Attribution and Analytics:</strong> Moving beyond likes to understand how EGC drives store-level performance and digital sentiment shifts.
              </li>
            </ul>
          </section>

          <section className="blog-post-section" id="takeaways">
            <h2>6. Strategic Takeaways for Brand Leaders and Franchisees</h2>
            <p>
              For brands looking to replicate the Papa John's Effect, the strategy must be <strong>governance, not control</strong>.
            </p>
            <ol>
              <li>
                <strong>Decentralize Creative:</strong> Move away from Brand Guidelines toward Vibe Guidelines. Let the employee's personality be the filter.
              </li>
              <li>
                <strong>Incentivize the Engine:</strong> Whether through corporate payroll or a content operating system like CrewCast, treat high-performing EGC creators as internal influencers.
              </li>
              <li>
                <strong>Embrace the Low-Fi Aesthetic:</strong> High production value is a signal of ad-ness. High-engagement content is a signal of real-ness.
              </li>
              <li>
                <strong>Build the Infrastructure:</strong> Platforms like CrewCast are the Operating System for this movement, providing the rails for discovery, tracking, and amplification without killing the organic spark.
              </li>
            </ol>
          </section>

          <section className="blog-post-section" id="compounding">
            <h2>7. The Compounding Advantage</h2>
            <p>
              Employees are the heartbeat of the brand. In 2026, the most successful brands will be those that stop trying to hire influence and start empowering it from the breakroom out.
            </p>
            <p>
              Brands that fail to systemize this will continue to pay a <strong>Polished Content Tax</strong>, spending more for less impact. Brands that adopt an EGC solution like CrewCast are building a defensible, high-margin media asset that lives on the balance sheet.
            </p>
          </section>

          <section className="blog-post-section" id="content-under-analysis" style={{ scrollMarginTop: 120 }}>
            <h2>Creators Referenced</h2>
            <p className="blog-small" style={{ marginTop: 0 }}>
              The creators referenced throughout this article.
            </p>
            <ol>
              {creatorLinks.map((v) => (
                <li key={v.id} style={{ marginBottom: 12 }}>
                  <a href={v.url} target="_blank" rel="noreferrer">
                    {v.label}
                  </a>
                  <div className="blog-small">Stats: {v.stats.join(' • ')}</div>
                </li>
              ))}
            </ol>
          </section>

          <section className="blog-post-section blog-post-cta">
            <h2>Want help implementing EGC?</h2>
            <p>Reach out and we'll show you how CrewCast can turn everyday moments into an always-on content engine.</p>
            <button className="blog-btn primary" onClick={() => navigate('/contact')}>
              Book a Strategy Audit
            </button>
          </section>
        </article>
      </main>
      <LandingFooter />
    </div>
  );
}
